import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface HtmlEmbedProps {
  html: string;
  minHeight?: string;
}

const HtmlEmbed: React.FC<HtmlEmbedProps> = ({ html, minHeight = '600px' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !html) return;

    const container = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let loadObserver: MutationObserver | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    // Loading overlay: só exibimos para embeds que geram um iframe (Superset/Tableau/PowerBI/Metabase).
    // Para HTML puro/texto sem iframe, loading fica false e nenhum spinner aparece.
    const expectsIframe = /embedDashboard|<iframe|tableau|powerbi|metabase|superset/i.test(html);
    setLoading(expectsIframe);

    // Limpar conteúdo anterior e scripts antigos
    container.innerHTML = '';
    
    // Remover scripts antigos do head que possam ter sido adicionados anteriormente
    const oldScripts = document.head.querySelectorAll('script[data-embed-script]');
    oldScripts.forEach((script) => script.remove());
    
    // Função para ajustar dimensões do Tableau
    const adjustTableauDimensions = () => {
      const tableauPlaceholder = container.querySelector('.tableauPlaceholder');
      const tableauViz = container.querySelector('.tableauViz');
      
      if (tableauPlaceholder && tableauViz) {
        // Forçar dimensões para ocupar toda a área
        const containerWidth = container.offsetWidth;
        const containerHeight = Math.max(container.offsetHeight || 800, 800);
        
        (tableauPlaceholder as HTMLElement).style.width = '100%';
        (tableauPlaceholder as HTMLElement).style.height = `${containerHeight}px`;
        (tableauPlaceholder as HTMLElement).style.minHeight = `${containerHeight}px`;
        
        (tableauViz as HTMLElement).style.width = '100%';
        (tableauViz as HTMLElement).style.height = `${containerHeight}px`;
        (tableauViz as HTMLElement).style.minHeight = `${containerHeight}px`;
        (tableauViz as HTMLElement).style.display = 'block';
        
        console.log('✅ Dimensões do Tableau ajustadas:', containerWidth, containerHeight);
      }
    };
    
    // Criar um div temporário para parsear o HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Separar scripts do resto do HTML
    const scripts: HTMLScriptElement[] = [];
    const scriptsElements = tempDiv.querySelectorAll('script');
    scriptsElements.forEach((script) => {
      scripts.push(script.cloneNode(true) as HTMLScriptElement);
      script.remove();
    });
    
    // Adicionar HTML sem scripts ao container
    container.innerHTML = tempDiv.innerHTML;

    // Detectar quando o iframe do embed for inserido para esconder o overlay de loading.
    // O SDK do Superset cria o <iframe> de forma assíncrona; observamos o container e,
    // ao achar o 1º iframe, aguardamos o evento "load" (+ folga p/ os gráficos renderizarem).
    if (expectsIframe) {
      const finishLoading = () => {
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => setLoading(false), 600);
      };
      loadObserver = new MutationObserver(() => {
        const iframe = container.querySelector('iframe');
        if (iframe) {
          // Desconecta após o 1º iframe: o re-embed do auto-refresh (a cada 60s) não
          // re-dispara o overlay, evitando o spinner "piscando" periodicamente.
          loadObserver?.disconnect();
          loadObserver = null;
          iframe.addEventListener('load', finishLoading, { once: true });
        }
      });
      loadObserver.observe(container, { childList: true, subtree: true });
      // Rede de segurança: esconde o overlay mesmo que o "load" não dispare.
      fallbackTimer = setTimeout(() => setLoading(false), 15000);
    }

    // Aguardar um pouco para garantir que o DOM foi atualizado
    const timeoutId = setTimeout(() => {
      // Executar scripts na ordem
      let scriptIndex = 0;
      
      const executeNextScript = () => {
        if (scriptIndex >= scripts.length) {
          // Todos os scripts foram executados
          console.log('✅ Todos os scripts foram executados');
          
          // Ajustar dimensões após scripts carregarem
          setTimeout(adjustTableauDimensions, 500);
          
          // Ajustar dimensões quando o container for redimensionado
          resizeObserver = new ResizeObserver(() => {
            adjustTableauDimensions();
          });
          
          if (container) {
            resizeObserver.observe(container);
          }
          
          return;
        }
        
        const oldScript = scripts[scriptIndex];
        const newScript = document.createElement('script');
        
        // Marcar script como embed para facilitar limpeza
        newScript.setAttribute('data-embed-script', 'true');
        
        // Copiar todos os atributos
        Array.from(oldScript.attributes).forEach((attr) => {
          if (attr.name !== 'src') {
            newScript.setAttribute(attr.name, attr.value);
          }
        });

        // Se o script tem src, carregar o arquivo externo
        if (oldScript.src) {
          newScript.src = oldScript.src;
          newScript.async = false;
          
          // Quando o script externo carregar, executar o próximo
          newScript.onload = () => {
            console.log('✅ Script externo carregado:', oldScript.src);
            scriptIndex++;
            executeNextScript();
          };
          
          newScript.onerror = () => {
            console.warn('⚠️ Erro ao carregar script:', oldScript.src);
            scriptIndex++;
            executeNextScript();
          };
          
          // Adicionar ao head para carregar
          document.head.appendChild(newScript);
        } else if (oldScript.textContent) {
          // Script inline - criar e executar
          newScript.textContent = oldScript.textContent;
          
          // Adicionar ao container para executar no contexto correto
          container.appendChild(newScript);
          
          // Aguardar um pouco antes de executar o próximo script
          scriptIndex++;
          setTimeout(() => {
            executeNextScript();
          }, 50);
        } else {
          scriptIndex++;
          executeNextScript();
        }
      };
      
      // Iniciar execução dos scripts
      if (scripts.length > 0) {
        executeNextScript();
      } else {
        // Se não há scripts, o HTML pode ser apenas iframe ou div
        console.log('ℹ️ HTML sem scripts (pode ser iframe simples)');
      }
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (hideTimer) clearTimeout(hideTimer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      // Desconectar observers
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (loadObserver) {
        loadObserver.disconnect();
      }
      // Limpar scripts quando o componente for desmontado
      if (container) {
        const scriptsToRemove = container.querySelectorAll('script');
        scriptsToRemove.forEach((script) => script.remove());
      }
      // Limpar scripts do head
      const embedScripts = document.head.querySelectorAll('script[data-embed-script]');
      embedScripts.forEach((script) => script.remove());
    };
  }, [html]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight }}>
      <Box
        ref={containerRef}
        sx={{
        width: '100%',
        height: '100%',
        minHeight,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Estilos para Tableau
        '& .tableauPlaceholder': {
          width: '100% !important',
          height: '100% !important',
          minHeight: '800px',
          position: 'relative',
          flex: 1,
        },
        '& .tableauViz': {
          width: '100% !important',
          height: '100% !important',
          minHeight: '800px',
          display: 'block !important',
        },
        // Estilos para PowerBI
        '& iframe[src*="powerbi"]': {
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        },
        // Estilos para Superset
        '& iframe[src*="superset"]': {
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        },
        // Estilos para Metabase
        '& iframe[src*="metabase"]': {
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        },
        // Estilos genéricos para iframes
        '& iframe': {
          width: '100%',
          height: '100%',
          border: 'none',
          flex: 1,
        },
        // Estilos para elementos embed genéricos
        '& [data-embed]': {
          width: '100%',
          height: '100%',
          flex: 1,
        },
        // Garantir que todos os elementos filhos ocupem espaço
        '& > *': {
          width: '100%',
          height: '100%',
          flex: 1,
        },
      }}
      />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'rgba(245,245,245,0.94)',
            transition: 'opacity .2s ease',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Carregando painel…
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HtmlEmbed;
